import React, { useState, useEffect } from 'react';
import { 
  HeartIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ChartBarIcon,
  BeakerIcon,
  UserGroupIcon,
  CalendarIcon,
  EyeIcon,
  ShieldCheckIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import { healthTestsApi, HealthStatistics, HealthAlert } from '../../api/healthTests';
import type { HealthSectionId } from './healthManagementNav';

interface HealthDashboardProps {
  statistics: HealthStatistics | null;
  alerts: HealthAlert[];
  workflowStats?: { active?: number; overdue?: number } | null;
  onRunMonitoring: () => Promise<void>;
  onRefresh: () => Promise<void>;
  onScheduleTest?: () => void;
  onHealthCheckup?: () => void;
  onViewReports?: () => void;
  onScheduleReminders?: () => void;
  onNavigateSection?: (section: HealthSectionId, view?: string) => void;
  onAddTest?: () => void;
  onStartChecklist?: () => void;
}

const HealthDashboard: React.FC<HealthDashboardProps> = ({
  statistics,
  alerts,
  workflowStats,
  onRunMonitoring,
  onRefresh,
  onScheduleTest,
  onHealthCheckup,
  onViewReports,
  onScheduleReminders,
  onNavigateSection,
  onAddTest,
  onStartChecklist,
}) => {
  const [loading, setLoading] = useState(false);
  const [lastMonitoringRun, setLastMonitoringRun] = useState<string | null>(null);
  const [localStatistics, setLocalStatistics] = useState<HealthStatistics | null>(statistics);

  useEffect(() => {
    if (!statistics) {
      fetchHealthStatistics();
    } else {
      setLocalStatistics(statistics);
    }
  }, [statistics]);

  const fetchHealthStatistics = async () => {
    try {
      setLoading(true);
      const response = await healthTestsApi.getStatistics();
      setLocalStatistics(response);
    } catch (error) {
      console.error('Error fetching health statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'HEALTHY': return 'text-green-600 bg-green-100';
      case 'UNDER_OBSERVATION': return 'text-yellow-600 bg-yellow-100';
      case 'SICK': return 'text-red-600 bg-red-100';
      case 'RECOVERING': return 'text-cyan-600 bg-blue-100';
      case 'QUARANTINE': return 'text-purple-600 bg-purple-100';
      case 'CRITICAL': return 'text-red-800 bg-red-200';
      default: return 'text-slate-600 bg-gray-100';
    }
  };

  const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-rose-100 text-rose-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-amber-100 text-amber-800 border-yellow-200';
      case 'LOW': return 'bg-cyan-100 text-cyan-800 border-blue-200';
      default: return 'bg-violet-100 text-violet-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!localStatistics) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Failed to load health statistics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-slate-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Total Birds</dt>
                  <dd className="text-lg font-medium text-slate-900">{localStatistics.totalBirds}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BeakerIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Recent Tests</dt>
                  <dd className="text-lg font-medium text-slate-900">{localStatistics.recentTests}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Pending Tests</dt>
                  <dd className="text-lg font-medium text-slate-900">{localStatistics.pendingTests}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Overdue Checkups</dt>
                  <dd className="text-lg font-medium text-slate-900">{localStatistics.overdueCheckups}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(workflowStats?.active || workflowStats?.overdue) ? (
        <div className="rounded-lg bg-white p-4 shadow ring-1 ring-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Disease workflows</h3>
              <p className="text-sm text-slate-600">
                {workflowStats?.active ?? 0} active · {workflowStats?.overdue ?? 0} overdue
              </p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateSection?.('care', 'workflows')}
              className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
            >
              View workflows →
            </button>
          </div>
        </div>
      ) : null}

      {/* Health Status Distribution */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
            Health Status Distribution
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {localStatistics.healthStatusDistribution.map((item) => (
              <div key={item.status} className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getHealthStatusColor(item.status)}`}>
                  {item.status.replace('_', ' ')}
                </div>
                <div className="mt-2 text-2xl font-bold text-slate-900">{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Smart Health Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
              Smart Health Alerts ({alerts.length})
            </h3>
            <div className="space-y-3">
              {alerts.slice(0, 10).map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 border rounded-lg ${getAlertSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {alert.severity === 'CRITICAL' && (
                        <FireIcon className="h-5 w-5 text-red-500" />
                      )}
                      {alert.severity === 'HIGH' && (
                        <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                      )}
                      {alert.severity === 'MEDIUM' && (
                        <ClockIcon className="h-5 w-5 text-yellow-500" />
                      )}
                      {alert.severity === 'LOW' && (
                        <EyeIcon className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">
                          {alert.alertType.replace('_', ' ')}
                        </p>
                        <span className="text-xs font-semibold">
                          {alert.severity}
                        </span>
                      </div>
                      <p className="text-sm mt-1">{alert.message}</p>
                      {alert.dueDate && (
                        <p className="text-xs text-slate-500 mt-1">
                          Due: {new Date(alert.dueDate).toLocaleDateString()}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {alert.severity === 'CRITICAL' && (
                          <>
                            <button
                              type="button"
                              onClick={onHealthCheckup}
                              className="inline-flex items-center rounded border border-transparent bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
                            >
                              <ShieldCheckIcon className="mr-1 h-3 w-3" />
                              Schedule vet
                            </button>
                            <button
                              type="button"
                              onClick={() => onNavigateSection?.('care', 'workflows')}
                              className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <HeartIcon className="mr-1 h-3 w-3" />
                              Start workflow
                            </button>
                          </>
                        )}
                        {alert.severity === 'HIGH' && (
                          <>
                            <button
                              type="button"
                              onClick={() => onNavigateSection?.('records', 'tests')}
                              className="inline-flex items-center rounded border border-transparent bg-orange-600 px-2 py-1 text-xs font-medium text-white hover:bg-orange-700"
                            >
                              <EyeIcon className="mr-1 h-3 w-3" />
                              View tests
                            </button>
                            <button
                              type="button"
                              onClick={onHealthCheckup}
                              className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <CalendarIcon className="mr-1 h-3 w-3" />
                              Schedule check
                            </button>
                          </>
                        )}
                        {(alert.severity === 'MEDIUM' || alert.severity === 'LOW') && (
                          <>
                            <button
                              type="button"
                              onClick={onScheduleReminders}
                              className="inline-flex items-center rounded border border-transparent bg-cyan-600 px-2 py-1 text-xs font-medium text-white hover:bg-cyan-700"
                            >
                              <CheckCircleIcon className="mr-1 h-3 w-3" />
                              Set reminder
                            </button>
                            <button
                              type="button"
                              onClick={onStartChecklist}
                              className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <ClockIcon className="mr-1 h-3 w-3" />
                              Run checklist
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {alerts.length > 10 && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => onNavigateSection?.('care', 'investigation')}
                    className="text-sm font-medium text-cyan-600 hover:text-cyan-500"
                  >
                    View all {alerts.length} alerts in care plans
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <button 
              type="button"
              onClick={onAddTest ?? onScheduleTest}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700"
            >
              <BeakerIcon className="h-4 w-4 mr-2" />
              Add health test
            </button>
            <button 
              type="button"
              onClick={onStartChecklist}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <HeartIcon className="h-4 w-4 mr-2" />
              Run checklist
            </button>
            <button 
              type="button"
              onClick={onHealthCheckup}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              Schedule checkup
            </button>
            <button 
              type="button"
              onClick={onViewReports}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <ChartBarIcon className="h-4 w-4 mr-2" />
              Reports
            </button>
            <button 
              type="button"
              onClick={onScheduleReminders}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <ClockIcon className="h-4 w-4 mr-2" />
              Reminders
            </button>
            <button
              type="button"
              onClick={() => onNavigateSection?.('reference', 'diseases')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              Disease catalog
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthDashboard; 